import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { LoadingState } from '../components/ui/AsyncBoundary';

/**
 * Permission gating in markup.
 *
 *   <Can do="device:assign"><Button …/></Can>
 *   <Can doAny={['alert:ack', 'alert:rules']}>…</Can>
 *
 * The strings are the server's permission strings, verbatim — `device:assign`
 * here is the same literal the API middleware checks. No role names appear
 * anywhere in the UI, so a role change is a server change and the client
 * follows automatically.
 */
export function Can({ do: permission, doAny, fallback = null, children }) {
  const { can, canAny } = useAuth();
  const allowed = doAny ? canAny(...doAny) : can(permission);
  return allowed ? children : fallback;
}

/** Route-level guard. Sends an unauthenticated visitor to sign in, and remembers where they were going. */
export function RequireAuth({ children }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingState className="h-screen" label="Restoring your session" />;
  if (status === 'anonymous') return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

/**
 * Route-level permission guard. A viewer who types /admin into the address bar
 * lands back on the dashboard rather than on a screen of failed requests.
 */
export function RequirePermission({ permission, children }) {
  const { can } = useAuth();
  if (!can(permission)) return <Navigate to="/" replace />;
  return children;
}
